import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { PublicLayout, ScrollReveal } from './components';
import { SITE_CONFIG, SERVICES } from './config';
import { SEOHead } from './SEOHead';

const ContactoPage = () => {
  const location = useLocation();
  const preselectedService = location.state?.service || '';
  const preselectedMenu = location.state?.menu || '';

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    tipoEvento: preselectedService ? 
      Object.keys(SERVICES).find(k => SERVICES[k].title === preselectedService) || '' : '',
    numPersonas: '',
    fecha: '',
    mensaje: preselectedMenu ? `Me interesa el menú: ${preselectedMenu}` : '',
    privacidad: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Por ahora simulamos el envío
      // En producción conectará con el backend para enviar email
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSubmitted(true);
      // Reset form
      setFormData({
        nombre: '', email: '', telefono: '', empresa: '',
        tipoEvento: '', numPersonas: '', fecha: '', mensaje: '', privacidad: false
      });
    } catch (err) {
      setError('Error al enviar el formulario. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <PublicLayout>
        <SEOHead page="contacto" />
        <section className="pt-24 lg:pt-32 pb-16 min-h-[70vh] flex items-center">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollReveal>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                ¡Mensaje enviado!
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Hemos recibido tu solicitud. Te contactaremos en menos de 24 horas 
                para prepararte un presupuesto personalizado.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/web"
                  className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors"
                >
                  Volver al inicio
                </Link>
                <a
                  href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors"
                >
                  O llámanos: {SITE_CONFIG.phone}
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOHead page="contacto" />
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/web" className="hover:text-emerald-600">Inicio</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">Contacto</span>
            </nav>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Solicita tu presupuesto
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              Cuéntanos sobre tu evento y te preparamos una propuesta personalizada sin compromiso.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Contenido */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Info de contacto */}
            <div className="lg:col-span-1">
              <ScrollReveal>
                <div className="sticky top-24 space-y-8">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                      Información de contacto
                    </h2>
                    
                    <div className="space-y-4">
                      <a 
                        href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                          <Phone className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{SITE_CONFIG.phone}</p>
                          <p className="text-sm text-gray-500">Llámanos directamente</p>
                        </div>
                      </a>

                      <a 
                        href={`mailto:${SITE_CONFIG.email}`}
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                          <Mail className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{SITE_CONFIG.email}</p>
                          <p className="text-sm text-gray-500">Escríbenos un email</p>
                        </div>
                      </a>

                      <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{SITE_CONFIG.address}</p>
                          <p className="text-sm text-gray-500">Servicio en toda la comunidad</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Lun - Vie: 9:00 - 18:00</p>
                          <p className="text-sm text-gray-500">Horario de atención</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-100">
                    <h3 className="font-semibold text-emerald-900 mb-2">
                      Respuesta rápida
                    </h3>
                    <p className="text-sm text-emerald-700">
                      Te enviamos un presupuesto detallado en menos de 24 horas laborables.
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Formulario */}
            <div className="lg:col-span-2">
              <ScrollReveal delay={100}>
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl">
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                        placeholder="Nombre de la empresa"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email corporativo *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                        placeholder="tu@empresa.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        required
                        value={formData.telefono}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                        placeholder="600 000 000"
                      />
                    </div>

                    <div>
                      <label htmlFor="tipoEvento" className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de evento
                      </label>
                      <select
                        id="tipoEvento"
                        name="tipoEvento"
                        value={formData.tipoEvento}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="desayunos">Desayuno / Coffee Break</option>
                        <option value="lunch">Lunch / Buffet</option>
                        <option value="cocteles">Cóctel</option>
                        <option value="vinoEspanol">Vino Español</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="numPersonas" className="block text-sm font-medium text-gray-700 mb-2">
                        Número de personas (aprox.)
                      </label>
                      <input
                        type="number"
                        id="numPersonas"
                        name="numPersonas"
                        min="1"
                        value={formData.numPersonas}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                        placeholder="40"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="fecha" className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha del evento (aproximada)
                      </label>
                      <input
                        type="date"
                        id="fecha"
                        name="fecha"
                        value={formData.fecha}
                        onChange={handleChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                        Cuéntanos más sobre tu evento
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        rows={4}
                        value={formData.mensaje}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all resize-none"
                        placeholder="Ubicación, horario, necesidades especiales, tipo de menú que te interesa..."
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          name="privacidad"
                          required
                          checked={formData.privacidad}
                          onChange={handleChange}
                          className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-600">
                          Acepto la{' '}
                          <a href="#" className="text-emerald-600 hover:underline">
                            política de privacidad
                          </a>{' '}
                          y el tratamiento de mis datos para recibir el presupuesto solicitado. *
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto px-8 py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 focus:ring-4 focus:ring-emerald-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Enviar solicitud
                        </>
                      )}
                    </button>
                  </div>

                  <p className="mt-4 text-sm text-gray-500">
                    * Campos obligatorios. Te responderemos en menos de 24 horas.
                  </p>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ContactoPage;
